// src/components/SavedShortlist.js

import React, { useState, useEffect } from 'react';
import CleanupModal from './CleanupModal';

/**
 * SavedShortlist
 *
 * The Lists tab. Two views:
 *   1. The list view — the member's lists, with their type (This week,
 *      Occasion, Watch), item counts, and an Open action. The unsaved
 *      items are shown above the lists, whenever there are any.
 *   2. The opened list — the items, grouped by state (available, segment
 *      off, unavailable), with the flags (on special, price up/down).
 */

export default function SavedShortlist({ kinId, onAddToDraft }) {
  const [lists, setLists] = useState([]);
  const [looseCount, setLooseCount] = useState(0);
  const [items, setItems] = useState([]);
  const [openList, setOpenList] = useState(null);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newList, setNewList] = useState({ name: '', type: 'scratch', occasionDate: '', comment: '' });
  const [showCleanup, setShowCleanup] = useState(false);

  useEffect(() => {
    if (openList) fetchItems(openList._id);
    else fetchLists();
  }, [kinId, openList]);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/lists/my/${kinId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load lists');
      const data = await res.json();
      setLists(data.lists || []);
      setLooseCount(data.looseCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async (listId) => {
    setLoading(true);
    setSelected({});
    try {
      const url = listId
        ? `/api/shortlist/my/${kinId}?listId=${listId}`
        : `/api/shortlist/my/${kinId}?listId=null`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load the list items');
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newList.name.trim()) return;
    try {
      const body = { name: newList.name.trim(), type: newList.type };
      if (newList.type === 'periodic' && newList.occasionDate) body.occasionDate = newList.occasionDate;
      if (newList.type === 'opportunity' && newList.comment) body.comment = newList.comment;

      const res = await fetch(`/api/lists/my/${kinId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create list');
      }
      setShowCreate(false);
      setNewList({ name: '', type: 'scratch', occasionDate: '', comment: '' });
      await fetchLists();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendListToDraft = async () => {
    if (!window.confirm(`Add all ${items.length} items from "${openList.name}" to your draft?`)) return;
    try {
      const token = localStorage.getItem('token');
      let added = 0;
      for (const it of items) {
        if (!['available', 'special', 'higher', 'lower'].includes(it.state)) continue;
        await fetch(`/api/member-drafts/my/${kinId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ productId: it.productId, quantity: 1 }),
        });
        added++;
      }
      setNotice(`${added} item${added !== 1 ? 's' : ''} sent to draft`);
      if (onAddToDraft) onAddToDraft();
      setTimeout(() => setNotice(null), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Delete this list and all its items?')) return;
    try {
      await fetch(`/api/lists/my/${kinId}/${listId}`, { method: 'DELETE', headers: authHeaders() });
      await fetchLists();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));

  const selectAllAvailable = () => {
    const next = {};
    for (const it of items) {
      if (['available', 'special', 'higher', 'lower'].includes(it.state)) next[it._id] = true;
    }
    setSelected(next);
  };

  const clearSelection = () => setSelected({});

  const handleAdd = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    try {
      for (const id of ids) {
        const item = items.find((i) => i._id === id);
        if (!item) continue;
        await fetch(`/api/member-drafts/my/${kinId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ productId: item.productId, quantity: 1 }),
        });
      }
      setNotice(`${ids.length} item${ids.length !== 1 ? 's' : ''} added to draft`);
      setSelected({});
      if (onAddToDraft) onAddToDraft();

      // Offer to clear the list, if all its items were added.
      if (openList && ids.length === items.length) {
        if (window.confirm(`All items sent to your draft. Delete the list "${openList.name}"?`)) {
          await fetch(`/api/lists/my/${kinId}/${openList._id}`, { method: 'DELETE', headers: authHeaders() });
          setOpenList(null);
        }
      }

      setTimeout(() => setNotice(null), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    if (!window.confirm(`Remove ${ids.length} item${ids.length !== 1 ? 's' : ''} from this list?`)) return;
    try {
      for (const id of ids) {
        await fetch(`/api/shortlist/my/${kinId}/${id}`, { method: 'DELETE', headers: authHeaders() });
      }
      setItems((p) => p.filter((i) => !ids.includes(i._id)));
      setSelected({});
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async (id) => {
    try {
      await fetch(`/api/shortlist/my/${kinId}/${id}`, { method: 'DELETE', headers: authHeaders() });
      setItems((p) => p.filter((i) => i._id !== id));
      setSelected((p) => { const n = { ...p }; delete n[id]; return n; });
    } catch (err) {
      setError(err.message);
    }
  };

  const groupOf = (state) => {
    if (['available', 'special', 'higher', 'lower'].includes(state)) return 'available';
    return state;
  };

  const badge = (it) => {
    if (it.state === 'special') return { text: `On special — save ${it.savingPct}%`, cls: 'bg-green-100 text-green-800' };
    if (it.state === 'higher') return { text: `Price up $${it.priceChange.toFixed(2)}`, cls: 'bg-red-100 text-red-800' };
    if (it.state === 'lower') return { text: `Price down $${Math.abs(it.priceChange).toFixed(2)}`, cls: 'bg-green-100 text-green-800' };
    if (it.state === 'segment_off') return { text: 'Segment off — ask your KC', cls: 'bg-gray-100 text-gray-500' };
    if (it.state === 'unavailable') return { text: 'No longer available', cls: 'bg-gray-100 text-gray-500' };
    return null;
  };

  const typeBadge = (type) => {
    if (type === 'periodic') return { text: 'Occasion', cls: 'bg-purple-100 text-purple-800' };
    if (type === 'opportunity') return { text: 'Watch', cls: 'bg-amber-100 text-amber-800' };
    return { text: 'This week', cls: 'bg-green-100 text-green-800' };
  };

  // --- The list view ---

  if (!openList) {
    if (loading) return <div className="text-gray-500 text-sm p-4">Loading lists...</div>;
    return (
      <div className="space-y-3">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs">{error}</div>}

        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Your lists</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCleanup(true)}
              disabled={lists.length < 2}
              title={lists.length < 2 ? 'You need at least two lists to clean up' : 'Merge several lists into one'}
              className="text-xs px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-40"
            >
              Clean up
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="text-xs px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700"
            >
              + New list
            </button>
          </div>
        </div>

        {/* Unsaved items — shown whenever there are any */}
        {looseCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm flex items-center justify-between">
            <span>
              You have {looseCount} unsaved item{looseCount !== 1 ? 's' : ''} in your shortlist.
            </span>
            <button
              onClick={() => setOpenList({ _id: null, name: 'Unsaved shortlist', type: 'scratch' })}
              className="text-amber-800 underline font-medium ml-2"
            >
              View them
            </button>
          </div>
        )}

        {lists.length === 0 && looseCount === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            No lists yet. Start researching in Product Research to build one.
          </div>
        )}

        <div className="space-y-2">
          {lists.map((l) => {
            const b = typeBadge(l.type);
            return (
              <div key={l._id} className="border rounded-lg p-3 flex items-center justify-between">
                <button
                  onClick={() => setOpenList(l)}
                  className="text-left flex-1 min-w-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{l.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${b.cls}`}>{b.text}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {l.itemCount} item{l.itemCount !== 1 ? 's' : ''}
                    {l.type === 'periodic' && l.occasionDate && (
                      <span className="ml-2">· {new Date(l.occasionDate).toLocaleDateString()}</span>
                    )}
                    {l.type === 'opportunity' && l.comment && (
                      <span className="ml-2 italic">· {l.comment}</span>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => handleDeleteList(l._id)}
                  className="text-xs text-red-600 hover:text-red-800 ml-2 px-1"
                >
                  Delete
                </button>
              </div>
            );
          })}
        </div>

        {/* Create modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-30 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-sm">
              <div className="p-4 space-y-3">
                <div className="font-semibold">Create a list</div>
                <input
                  type="text"
                  value={newList.name}
                  onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                  placeholder="List name (e.g. This week, Christmas, Watch list)"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  autoFocus
                />
                <div className="space-y-1">
                  <div className="text-xs text-gray-600 font-medium">Type</div>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { key: 'scratch', label: 'This week' },
                      { key: 'periodic', label: 'Occasion' },
                      { key: 'opportunity', label: 'Watch' },
                    ].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setNewList({ ...newList, type: t.key })}
                        className={`px-2 py-1 text-xs rounded border ${
                          newList.type === t.key
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                {newList.type === 'periodic' && (
                  <input
                    type="date"
                    value={newList.occasionDate}
                    onChange={(e) => setNewList({ ...newList, occasionDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                )}
                {newList.type === 'opportunity' && (
                  <input
                    type="text"
                    value={newList.comment}
                    onChange={(e) => setNewList({ ...newList, comment: e.target.value })}
                    placeholder="Why are you watching these? (optional)"
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                )}
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
                  <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700">
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      {showCleanup && (
        <CleanupModal
          kinId={kinId}
          lists={lists}
          onClose={() => setShowCleanup(false)}
          onApplied={() => fetchLists()}
        />
      )}
      </div>
    );
  }

  // --- The opened list ---

  if (loading) return <div className="text-gray-500 text-sm p-4">Loading items...</div>;

  const groups = { available: [], segment_off: [], unavailable: [] };
  for (const it of items) groups[groupOf(it.state)].push(it);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const renderItem = (it) => {
    const isSelectable = groupOf(it.state) === 'available';
    const b = badge(it);
    return (
      <div key={it._id} className="flex items-start gap-2 border rounded-lg p-2">
        <input
          type="checkbox"
          disabled={!isSelectable}
          checked={!!selected[it._id]}
          onChange={() => toggle(it._id)}
          className="mt-1 w-4 h-4"
        />
        {it.imageUrl && (
          <img src={it.imageUrl} alt="" className="w-10 h-10 object-contain rounded"
            onError={(e) => { e.target.style.display = 'none'; }} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{it.name}</div>
          <div className="text-xs text-gray-500">
            {it.supplierId.replace('vhub:', '')} · ${(it.currentPrice ?? it.lastKnownPrice)?.toFixed(2)}
            {it.priceChange !== null && it.priceChange !== 0 && (
              <span className={it.priceChange > 0 ? 'text-red-600 ml-1' : 'text-green-600 ml-1'}>
                ({it.priceChange > 0 ? '+' : ''}${it.priceChange.toFixed(2)} vs saved)
              </span>
            )}
          </div>
          {b && <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded ${b.cls}`}>{b.text}</span>}
        </div>
        <button onClick={() => handleRemove(it._id)} className="text-xs text-red-600 hover:text-red-800 px-1">✕</button>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={() => setOpenList(null)} className="text-xs text-gray-600 hover:text-gray-900">
          ← Back to lists
        </button>
        <span className="font-semibold text-sm truncate">{openList.name}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeBadge(openList.type).cls}`}>
          {typeBadge(openList.type).text}
        </span>
      </div>

      {notice && <div className="bg-green-50 border border-green-200 text-green-800 p-2 rounded text-xs">{notice}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs">{error}</div>}

      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          This list is empty. Add products in Product Research.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={selectAllAvailable} className="text-xs px-2 py-1 border rounded hover:bg-gray-50">Select all available</button>
            <button onClick={clearSelection} className="text-xs px-2 py-1 border rounded hover:bg-gray-50">Clear selection</button>
            <span className="text-xs text-gray-500">{selectedCount} selected</span>
            <button
              onClick={handleDeleteSelected}
              disabled={selectedCount === 0}
              className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-40"
            >
              Delete selected
            </button>
            <button
              onClick={handleAdd}
              disabled={selectedCount === 0}
              className="ml-auto text-xs px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300"
            >
              Add {selectedCount > 0 ? selectedCount : ''} to draft
            </button>
          </div>

          {groups.available.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">Available ({groups.available.length})</div>
              <div className="space-y-2">{groups.available.map(renderItem)}</div>
            </div>
          )}
          {groups.segment_off.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">Segment off ({groups.segment_off.length})</div>
              <div className="space-y-2">{groups.segment_off.map(renderItem)}</div>
            </div>
          )}
          {groups.unavailable.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">No longer available ({groups.unavailable.length})</div>
              <div className="space-y-2">{groups.unavailable.map(renderItem)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
